import {
  AlertCircle,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Heart,
  PackageCheck,
  Search,
  ShoppingCart,
  SlidersHorizontal,
  Star,
  Truck,
  X,
  Zap,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Slider from "@mui/material/Slider";
import {
  REF_TABS,
  SORT_OPTIONS,
  mapReferralProductToCard,
  formatPrice,
  type RefProductCardData,
  type RefSort,
  type RefTabId,
} from "./RefProductTypes";
import "./RefProduct.css";
import HomeNavbar from "../../globalcomponents/HomeNavbar/HomeNavbar";
import HomeSecondaryNavbar from "../../globalcomponents/HomeSecondaryNavbar/HomeSecondaryNavbar";
import { useCart } from "../../../context/CartContext";
import { useWishlist } from "../../../context/WishlistContext";
import { useAppDispatch, useAppSelector } from "../../../Redux/hooks";
import { fetchReferralProducts } from "../../../Redux/RefProduct/slice";
import { fetchCategoryTree } from "../../../Redux/ecomCatalog/slice";
import MobileDetailNavbar from "../../globalcomponents/mobile/MobileDetailNavbar/MobileDetailNavbar";

const PAGE_SIZE = 24;
const SEARCH_DEBOUNCE_MS = 300;

type MobileFilterSection =
  | "sort"
  | "department"
  | "delivery"
  | "price"
  | "discount"
  | "rating"
  | "brands";

const refProductMobileFilterSections: {
  id: MobileFilterSection;
  label: string;
}[] = [
  { id: "sort", label: "Sort" },
  { id: "department", label: "Department" },
  { id: "delivery", label: "Delivery" },
  { id: "price", label: "Price" },
  { id: "discount", label: "Discount" },
  { id: "rating", label: "Customer Reviews" },
  { id: "brands", label: "Brands" },
];

const RefProduct = () => {
  const navigate = useNavigate();
  const { addToCart, isProductInCart, cartCount } = useCart();
  const { toggleWishlist, isItemWishlisted } = useWishlist();
  const dispatch = useAppDispatch();
  const { products, loading, error } = useAppSelector(
    (state) => state.refProduct,
  );
  const { tree } = useAppSelector((state) => state.ecomCatalog);
  const tabsRef = useRef<HTMLDivElement | null>(null);

  // ─── UI-only state ─────────────────────────────────────────────────────────
  const [browsingHistoryOpen, setBrowsingHistoryOpen] = useState(false);
  const [selectedColors, setSelectedColors] = useState<Record<string, number>>(
    {},
  );

  // ─── Filter & Search state ────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<RefTabId>("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [superCategory, setSuperCategory] = useState<string | undefined>(
    undefined,
  );
  const [brandNames, setBrandNames] = useState<string[]>([]);
  const [minPrice, setMinPrice] = useState<number | undefined>(undefined);
  const [maxPrice, setMaxPrice] = useState<number | undefined>(undefined);
  const [minDiscount, setMinDiscount] = useState<number | undefined>(undefined);
  const [minRating, setMinRating] = useState<number | undefined>(undefined);
  const [freeDelivery, setFreeDelivery] = useState(false);
  const [fastDelivery, setFastDelivery] = useState(false);
  const [nextDayDelivery, setNextDayDelivery] = useState(false);
  const [sort, setSort] = useState<RefSort>("discount");
  const [page, setPage] = useState(1);

  // ─── Mobile filter UI state ────────────────────────────────────────────────
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [mobileFilterSection, setMobileFilterSection] =
    useState<MobileFilterSection>("sort");
  const [mobileFilterSearch, setMobileFilterSearch] = useState("");

  // Debounce search
  useEffect(() => {
    const t = window.setTimeout(
      () => setDebouncedSearch(search.trim()),
      SEARCH_DEBOUNCE_MS,
    );
    return () => window.clearTimeout(t);
  }, [search]);

  // Reset to page 1 whenever any filter/tab/sort changes
  useEffect(() => {
    setPage(1);
  }, [
    activeTab,
    debouncedSearch,
    superCategory,
    brandNames,
    minPrice,
    maxPrice,
    minDiscount,
    minRating,
    freeDelivery,
    fastDelivery,
    nextDayDelivery,
    sort,
  ]);

  // Fetch referral products when activeTab changes
  useEffect(() => {
    const targetTag = REF_TABS.find((t) => t.id === activeTab)?.tag || "";
    dispatch(fetchReferralProducts(targetTag ? { tag: targetTag } : undefined));
  }, [dispatch, activeTab]);

  // Fetch category tree if needed
  useEffect(() => {
    if (tree.length === 0) {
      dispatch(fetchCategoryTree());
    }
  }, [dispatch, tree.length]);

  // Lock body scroll when mobile filter is open
  useEffect(() => {
    if (!mobileFilterOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMobileFilterOpen(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [mobileFilterOpen]);

  // Convert raw products to card data
  const allCards: RefProductCardData[] = useMemo(() => {
    return (products || []).map(mapReferralProductToCard);
  }, [products]);

  // Calculate dynamic facets from products
  const facets = useMemo(() => {
    let minP = Infinity;
    let maxP = 0;
    const brandMap = new Map<string, number>();
    const deptMap = new Map<string, number>();

    allCards.forEach((c) => {
      if (c.price < minP) minP = c.price;
      if (c.price > maxP) maxP = c.price;

      if (c.brandName) {
        brandMap.set(c.brandName, (brandMap.get(c.brandName) || 0) + 1);
      }
      const dept = c.superCategory || c.category;
      if (dept) {
        deptMap.set(dept, (deptMap.get(dept) || 0) + 1);
      }
    });

    const brands = Array.from(brandMap.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);

    const departments = Array.from(deptMap.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);

    const discountBuckets = [10, 20, 30, 40, 50].map((tier) => ({
      minDiscount: tier,
      label: `${tier}% off or more`,
      count: allCards.filter((c) => c.discountPercent >= tier).length,
    }));

    const ratingBuckets = [
      {
        minRating: 4,
        label: "4★ & above",
        count: allCards.filter((c) => c.rating >= 4).length,
      },
      {
        minRating: 3,
        label: "3★ & above",
        count: allCards.filter((c) => c.rating >= 3).length,
      },
    ];

    return {
      priceRange: {
        min: Number.isFinite(minP) ? minP : 0,
        max: maxP > 0 ? maxP : 5000,
      },
      brands,
      departments,
      discountBuckets,
      ratingBuckets,
    };
  }, [allCards]);

  const rangeMin = facets.priceRange.min;
  const rangeMax = Math.max(facets.priceRange.max, rangeMin + 1);

  const [localMin, setLocalMin] = useState<number>(minPrice ?? rangeMin);
  const [localMax, setLocalMax] = useState<number>(maxPrice ?? rangeMax);

  useEffect(() => {
    setLocalMin(minPrice ?? rangeMin);
  }, [minPrice, rangeMin]);

  useEffect(() => {
    setLocalMax(maxPrice ?? rangeMax);
  }, [maxPrice, rangeMax]);

  // Client-side filtering
  const filteredCards = useMemo(() => {
    let result = [...allCards];

    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      result = result.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.description.toLowerCase().includes(q) ||
          c.brandName.toLowerCase().includes(q) ||
          (c.superCategory && c.superCategory.toLowerCase().includes(q)) ||
          (c.category && c.category.toLowerCase().includes(q)),
      );
    }

    if (superCategory) {
      result = result.filter(
        (c) =>
          c.superCategory === superCategory || c.category === superCategory,
      );
    }

    if (brandNames.length > 0) {
      result = result.filter((c) => brandNames.includes(c.brandName));
    }

    if (minPrice !== undefined) {
      result = result.filter((c) => c.price >= minPrice);
    }

    if (maxPrice !== undefined) {
      result = result.filter((c) => c.price <= maxPrice);
    }

    if (minDiscount !== undefined) {
      result = result.filter((c) => c.discountPercent >= minDiscount);
    }

    if (minRating !== undefined) {
      result = result.filter((c) => c.rating >= minRating);
    }

    if (freeDelivery) {
      result = result.filter((c) => c.freeDelivery);
    }

    if (fastDelivery) {
      result = result.filter((c) => c.fastDelivery);
    }

    if (nextDayDelivery) {
      result = result.filter((c) => c.nextDayDelivery);
    }

    // Sorting
    result.sort((a, b) => {
      if (sort === "discount") {
        return b.discountPercent - a.discountPercent || b.savingsAmount - a.savingsAmount;
      }
      if (sort === "rating") {
        return b.rating - a.rating || b.reviewCount - a.reviewCount;
      }
      if (sort === "price_asc") {
        return a.price - b.price;
      }
      if (sort === "price_desc") {
        return b.price - a.price;
      }
      return 0; // newest / default
    });

    return result;
  }, [
    allCards,
    debouncedSearch,
    superCategory,
    brandNames,
    minPrice,
    maxPrice,
    minDiscount,
    minRating,
    freeDelivery,
    fastDelivery,
    nextDayDelivery,
    sort,
  ]);

  // Pagination
  const totalItems = filteredCards.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
  const currentCards = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredCards.slice(start, start + PAGE_SIZE);
  }, [filteredCards, page]);

  const pageNumbers = useMemo(() => {
    if (totalPages <= 1) return [];
    const maxVisible = 5;
    let start = Math.max(1, page - Math.floor(maxVisible / 2));
    const end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, [totalPages, page]);

  // ─── Handlers ──────────────────────────────────────────────────────────────
  const resetFilters = () => {
    setSearch("");
    setDebouncedSearch("");
    setSuperCategory(undefined);
    setBrandNames([]);
    setMinPrice(undefined);
    setMaxPrice(undefined);
    setLocalMin(rangeMin);
    setLocalMax(rangeMax);
    setMinDiscount(undefined);
    setMinRating(undefined);
    setFreeDelivery(false);
    setFastDelivery(false);
    setNextDayDelivery(false);
    setSort("discount");
  };

  const clearFilters = () => {
    resetFilters();
    setActiveTab("all");
  };

  const handleTabChange = (tabId: RefTabId) => {
    setActiveTab(tabId);
    resetFilters();
  };

  const toggleBrand = (brandName: string) => {
    setBrandNames((prev) =>
      prev.includes(brandName)
        ? prev.filter((b) => b !== brandName)
        : [...prev, brandName],
    );
  };

  const handleWishlistToggle = async (
    e: React.MouseEvent,
    card: RefProductCardData,
  ) => {
    e.stopPropagation();
    const targetId = card.productId || card.id;
    try {
      await toggleWishlist(targetId, { ssin: card.ssin });
    } catch (err) {
      console.error("Failed to toggle wishlist in RefProduct:", err);
    }
  };

  const selectColor = (productId: string, colorIndex: number) => {
    setSelectedColors((prev) => ({ ...prev, [productId]: colorIndex }));
  };

  const scrollTabs = (direction: "left" | "right") => {
    tabsRef.current?.scrollBy({
      left: direction === "left" ? -420 : 420,
      behavior: "smooth",
    });
  };

  const handleProductClick = (card: RefProductCardData) => {
    navigate(`/Refproductdetails/${card.productId || card.id}`);
  };

  const handleAddToCart = (card: RefProductCardData) => {
    if (isProductInCart(card.productId || card.id)) return;
    addToCart({
      productId: card.productId || card.id,
      title: card.name,
      description: card.description,
      image: card.image,
      price: card.price,
      mrp: card.mrp,
      isMLMProduct: true,
    });
  };

  const goToPage = (next: number) => {
    if (next < 1 || next > totalPages || next === page) return;
    setPage(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ─── Mobile filter helpers ────────────────────────────────────────────────
  const openMobileFilter = (section: MobileFilterSection = "sort") => {
    setMobileFilterSection(section);
    setMobileFilterSearch("");
    setMobileFilterOpen(true);
  };

  const mobileFilterMatchesSearch = (label: string) => {
    const query = mobileFilterSearch.trim().toLowerCase();
    if (!query) return true;
    return label.toLowerCase().includes(query);
  };

  const getMobileFilterCount = (section: MobileFilterSection): number => {
    if (section === "sort") return sort !== "discount" ? 1 : 0;
    if (section === "department") return superCategory !== undefined ? 1 : 0;
    if (section === "delivery")
      return (
        (freeDelivery ? 1 : 0) +
        (fastDelivery ? 1 : 0) +
        (nextDayDelivery ? 1 : 0)
      );
    if (section === "price")
      return minPrice !== undefined || maxPrice !== undefined ? 1 : 0;
    if (section === "discount") return minDiscount !== undefined ? 1 : 0;
    if (section === "rating") return minRating !== undefined ? 1 : 0;
    return brandNames.length;
  };

  const activeMobileFilterCount = refProductMobileFilterSections.reduce(
    (total, section) => total + getMobileFilterCount(section.id),
    0,
  );

  const renderMobileFilterOptions = () => {
    if (mobileFilterSection === "sort") {
      return (
        <div className="ref-product-mobile-sheet-options">
          {SORT_OPTIONS.filter((option) =>
            mobileFilterMatchesSearch(option.label),
          ).map((option) => (
            <label
              key={option.value}
              className={`ref-product-mobile-sheet-option ${
                sort === option.value ? "active" : ""
              }`}
            >
              <input
                type="radio"
                name="ref-mobile-sort"
                checked={sort === option.value}
                onChange={() => setSort(option.value)}
              />
              <span>{option.label}</span>
              {sort === option.value && <Check size={16} strokeWidth={2.4} />}
            </label>
          ))}
        </div>
      );
    }

    if (mobileFilterSection === "department") {
      const allOption = {
        name: "All Departments",
        count: allCards.length,
      };
      const deptList =
        facets.departments.length > 0
          ? facets.departments
          : tree.map((n) => ({ name: n.name, count: n.count }));

      return (
        <div className="ref-product-mobile-sheet-options">
          {[allOption, ...deptList]
            .filter((node) => mobileFilterMatchesSearch(node.name))
            .map((node) => {
              const isAll = node.name === "All Departments";
              const checked = isAll
                ? superCategory === undefined
                : superCategory === node.name;
              return (
                <label
                  key={node.name}
                  className={`ref-product-mobile-sheet-option ${
                    checked ? "active" : ""
                  }`}
                >
                  <input
                    type="radio"
                    name="ref-mobile-department"
                    checked={checked}
                    onChange={() =>
                      setSuperCategory(isAll ? undefined : node.name)
                    }
                  />
                  <span>
                    {node.name}
                    {node.count !== undefined && <> ({node.count})</>}
                  </span>
                  {checked && <Check size={16} strokeWidth={2.4} />}
                </label>
              );
            })}
        </div>
      );
    }

    if (mobileFilterSection === "delivery") {
      const deliveryOpts = [
        {
          label: "Free Delivery",
          value: freeDelivery,
          set: setFreeDelivery,
        },
        {
          label: "Fast Delivery",
          value: fastDelivery,
          set: setFastDelivery,
        },
        {
          label: "Next Day Delivery",
          value: nextDayDelivery,
          set: setNextDayDelivery,
        },
      ];
      return (
        <div className="ref-product-mobile-sheet-options">
          {deliveryOpts
            .filter((opt) => mobileFilterMatchesSearch(opt.label))
            .map((opt) => (
              <label
                key={opt.label}
                className={`ref-product-mobile-sheet-option ${
                  opt.value ? "active" : ""
                }`}
              >
                <input
                  type="checkbox"
                  checked={opt.value}
                  onChange={() => opt.set(!opt.value)}
                />
                <span>{opt.label}</span>
                {opt.value && <Check size={16} strokeWidth={2.4} />}
              </label>
            ))}
        </div>
      );
    }

    if (mobileFilterSection === "price") {
      return (
        <div className="ref-product-mobile-filter-price-panel">
          <div className="ref-product-price-slider-wrap">
            <div className="ref-product-price-range-values">
              <span>Range:</span>
              <strong>
                ₹{formatPrice(localMin)} – ₹{formatPrice(localMax)}
              </strong>
            </div>
            <div style={{ padding: "0 8px" }}>
              <Slider
                value={[localMin, localMax]}
                min={rangeMin}
                max={rangeMax}
                step={1}
                onChange={(_, newValue) => {
                  if (Array.isArray(newValue)) {
                    setLocalMin(newValue[0]);
                    setLocalMax(newValue[1]);
                    setMinPrice(
                      newValue[0] <= rangeMin ? undefined : newValue[0],
                    );
                    setMaxPrice(
                      newValue[1] >= rangeMax ? undefined : newValue[1],
                    );
                  }
                }}
                disableSwap
                sx={{
                  color: "#173c72",
                  height: 6,
                  padding: "12px 0",
                  "& .MuiSlider-track": {
                    height: 6,
                    borderRadius: "999px",
                    backgroundColor: "#173c72",
                    border: "none",
                  },
                  "& .MuiSlider-rail": {
                    height: 6,
                    borderRadius: "999px",
                    backgroundColor: "#e2e8f0",
                    opacity: 1,
                  },
                  "& .MuiSlider-thumb": {
                    height: 18,
                    width: 18,
                    backgroundColor: "#173c72",
                    border: "2.5px solid #ffffff",
                    boxShadow: "0 2px 6px rgba(15, 23, 42, 0.25)",
                    "&:hover": {
                      boxShadow: "0 0 0 7px rgba(23, 60, 114, 0.12)",
                    },
                    "&.Mui-active": {
                      boxShadow: "0 0 0 10px rgba(23, 60, 114, 0.18)",
                    },
                  },
                }}
              />
            </div>
            <div className="ref-product-price-range-limits">
              <span>₹{formatPrice(rangeMin)}</span>
              <span>₹{formatPrice(rangeMax)}</span>
            </div>
          </div>
          {(minPrice !== undefined || maxPrice !== undefined) && (
            <button
              type="button"
              className="ref-product-inline-clear"
              onClick={() => {
                setMinPrice(undefined);
                setMaxPrice(undefined);
                setLocalMin(rangeMin);
                setLocalMax(rangeMax);
              }}
            >
              Clear price
            </button>
          )}
        </div>
      );
    }

    if (mobileFilterSection === "discount") {
      return (
        <div className="ref-product-mobile-sheet-options">
          {mobileFilterMatchesSearch("Any discount") && (
            <label
              className={`ref-product-mobile-sheet-option ${
                minDiscount === undefined ? "active" : ""
              }`}
            >
              <input
                type="radio"
                name="ref-mobile-discount"
                checked={minDiscount === undefined}
                onChange={() => setMinDiscount(undefined)}
              />
              <span>Any discount</span>
              {minDiscount === undefined && (
                <Check size={16} strokeWidth={2.4} />
              )}
            </label>
          )}
          {facets.discountBuckets
            .filter((b) => mobileFilterMatchesSearch(b.label))
            .map((bucket) => (
              <label
                key={bucket.minDiscount}
                className={`ref-product-mobile-sheet-option ${
                  minDiscount === bucket.minDiscount ? "active" : ""
                }`}
              >
                <input
                  type="radio"
                  name="ref-mobile-discount"
                  checked={minDiscount === bucket.minDiscount}
                  onChange={() => setMinDiscount(bucket.minDiscount)}
                />
                <span>
                  {bucket.label}
                  {bucket.count !== undefined && (
                    <small> ({bucket.count})</small>
                  )}
                </span>
                {minDiscount === bucket.minDiscount && (
                  <Check size={16} strokeWidth={2.4} />
                )}
              </label>
            ))}
        </div>
      );
    }

    if (mobileFilterSection === "rating") {
      return (
        <div className="ref-product-mobile-sheet-options">
          {facets.ratingBuckets
            .filter((b) => mobileFilterMatchesSearch(b.label))
            .map((bucket) => (
              <button
                type="button"
                key={bucket.minRating}
                className={`ref-product-mobile-rating-option ${
                  minRating === bucket.minRating ? "active" : ""
                }`}
                onClick={() =>
                  setMinRating(
                    minRating === bucket.minRating
                      ? undefined
                      : bucket.minRating,
                  )
                }
              >
                <span className="ref-product-mobile-rating-stars">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      size={14}
                      strokeWidth={1.8}
                      fill={
                        star <= bucket.minRating ? "currentColor" : "none"
                      }
                    />
                  ))}
                </span>
                <strong>
                  {bucket.label}
                  {bucket.count !== undefined && (
                    <small> ({bucket.count})</small>
                  )}
                </strong>
                {minRating === bucket.minRating && (
                  <Check size={16} strokeWidth={2.4} />
                )}
              </button>
            ))}
        </div>
      );
    }

    // Brands section
    return (
      <div className="ref-product-mobile-sheet-options">
        {facets.brands
          .filter((brand) => mobileFilterMatchesSearch(brand.name))
          .map((brand) => (
            <label
              key={brand.name}
              className={`ref-product-mobile-sheet-option ${
                brandNames.includes(brand.name) ? "active" : ""
              }`}
            >
              <input
                type="checkbox"
                checked={brandNames.includes(brand.name)}
                onChange={() => toggleBrand(brand.name)}
              />
              <span>
                {brand.name}
                {brand.count !== undefined && (
                  <small> ({brand.count})</small>
                )}
              </span>
            </label>
          ))}
      </div>
    );
  };

  const activeTabLabel =
    REF_TABS.find((t) => t.id === activeTab)?.label ?? "Referral Products";
  const isInitialLoading = loading && allCards.length === 0;

  return (
    <>
      {/* Desktop navbars — hidden on mobile */}
      <div className="ref-product-desktop-navbar">
        <HomeNavbar variant="home" />
      </div>
      <div className="ref-product-desktop-secondary-navbar">
        <HomeSecondaryNavbar
          variant="detail"
          browsingHistoryOpen={browsingHistoryOpen}
          onBrowsingHistoryClick={() =>
            setBrowsingHistoryOpen((current) => !current)
          }
        />
      </div>

      {/* Mobile navbar — hidden on desktop */}
      <div className="ref-product-mobile-navbar">
        <MobileDetailNavbar
          cartCount={cartCount}
          searchPlaceholder="Search referral products"
          fallbackPath="/HomePage"
        />
      </div>

      {/* Sticky mobile tabs bar — hidden on desktop */}
      <div className="ref-product-mobile-tabs-bar">
        <button
          type="button"
          className={`ref-product-mobile-filter-trigger ${
            activeMobileFilterCount > 0 ? "active" : ""
          }`}
          onClick={() => openMobileFilter("sort")}
        >
          <SlidersHorizontal size={16} strokeWidth={2} />
          <span>Sort &amp; Filter</span>
          {activeMobileFilterCount > 0 && (
            <strong>{activeMobileFilterCount}</strong>
          )}
        </button>
        <div className="ref-product-mobile-tabs-scroll">
          {REF_TABS.map((tab) => (
            <button
              type="button"
              key={tab.id}
              className={activeTab === tab.id ? "active" : ""}
              onClick={() => handleTabChange(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <main className="ref-product-page">
        <div className="ref-product-shell">
          {/* Breadcrumbs */}
          <div className="ref-product-breadcrumb">
            <button type="button" onClick={() => navigate("/HomePage")}>
              Home
            </button>
            <ChevronRight size={15} />
            <strong>Referral Products</strong>
          </div>

          {/* Hero Banner */}
          <section className="ref-product-hero">
            <div>
              <span>EXCLUSIVE REFERRAL REWARDS</span>
              <h1>Referral Products</h1>
              <p>
                Shop curated products with verified referral benefits, instant
                shopping voucher rewards and high savings.
              </p>
            </div>
            <label className="ref-product-search">
              <Search size={20} />
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search referral products"
              />
            </label>
          </section>

          {/* Tabs Strip with Left/Right Arrows */}
          <section className="ref-product-tabs-wrap">
            <button
              type="button"
              className="ref-product-tab-arrow"
              onClick={() => scrollTabs("left")}
              aria-label="Scroll referral tabs left"
            >
              <ChevronLeft size={20} />
            </button>
            <div className="ref-product-tabs" ref={tabsRef}>
              {REF_TABS.map((tab) => (
                <button
                  type="button"
                  key={tab.id}
                  className={activeTab === tab.id ? "active" : ""}
                  onClick={() => handleTabChange(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="ref-product-tab-arrow"
              onClick={() => scrollTabs("right")}
              aria-label="Scroll referral tabs right"
            >
              <ChevronRight size={20} />
            </button>
          </section>

          {/* Main 2-Column Content */}
          <div className="ref-product-content">
            {/* Desktop Filters Sidebar */}
            <aside className="ref-product-filters">
              <div className="ref-product-filter-head">
                <div>
                  <SlidersHorizontal size={20} />
                  <strong>Filters</strong>
                </div>
                <button type="button" onClick={clearFilters}>
                  Clear all
                </button>
              </div>

              {/* Department */}
              <div className="ref-product-filter-section department-section">
                <h2>Department</h2>
                <div className="ref-product-department-list">
                  <label className="ref-product-filter-option">
                    <input
                      type="radio"
                      name="ref-department"
                      checked={superCategory === undefined}
                      onChange={() => setSuperCategory(undefined)}
                    />
                    <span>
                      All Departments <small>({allCards.length})</small>
                    </span>
                  </label>
                  {(facets.departments.length > 0
                    ? facets.departments
                    : tree.map((n) => ({ name: n.name, count: n.count }))
                  ).map((node) => (
                    <label
                      key={node.name}
                      className="ref-product-filter-option"
                    >
                      <input
                        type="radio"
                        name="ref-department"
                        checked={superCategory === node.name}
                        onChange={() => setSuperCategory(node.name)}
                      />
                      <span>
                        {node.name}
                        {node.count !== undefined && (
                          <small> ({node.count})</small>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Delivery */}
              <div className="ref-product-filter-section">
                <h2>Delivery</h2>
                {[
                  {
                    label: "Free Delivery",
                    value: freeDelivery,
                    set: setFreeDelivery,
                  },
                  {
                    label: "Fast Delivery",
                    value: fastDelivery,
                    set: setFastDelivery,
                  },
                  {
                    label: "Next Day Delivery",
                    value: nextDayDelivery,
                    set: setNextDayDelivery,
                  },
                ].map((opt) => (
                  <label
                    className="ref-product-filter-option"
                    key={opt.label}
                  >
                    <input
                      type="checkbox"
                      checked={opt.value}
                      onChange={() => opt.set(!opt.value)}
                    />
                    <span>{opt.label}</span>
                  </label>
                ))}
              </div>

              {/* Price Range */}
              <div className="ref-product-filter-section">
                <h2>Price</h2>
                <div className="ref-product-price-slider-wrap">
                  <div className="ref-product-price-range-values">
                    <span>Range:</span>
                    <strong>
                      ₹{formatPrice(localMin)} – ₹{formatPrice(localMax)}
                    </strong>
                  </div>
                  <div style={{ padding: "0 8px" }}>
                    <Slider
                      value={[localMin, localMax]}
                      min={rangeMin}
                      max={rangeMax}
                      step={1}
                      onChange={(_, newValue) => {
                        if (Array.isArray(newValue)) {
                          setLocalMin(newValue[0]);
                          setLocalMax(newValue[1]);
                          setMinPrice(
                            newValue[0] <= rangeMin ? undefined : newValue[0],
                          );
                          setMaxPrice(
                            newValue[1] >= rangeMax ? undefined : newValue[1],
                          );
                        }
                      }}
                      disableSwap
                      sx={{
                        color: "#173c72",
                        height: 6,
                        padding: "12px 0",
                        "& .MuiSlider-track": {
                          height: 6,
                          borderRadius: "999px",
                          backgroundColor: "#173c72",
                          border: "none",
                        },
                        "& .MuiSlider-rail": {
                          height: 6,
                          borderRadius: "999px",
                          backgroundColor: "#e2e8f0",
                          opacity: 1,
                        },
                        "& .MuiSlider-thumb": {
                          height: 18,
                          width: 18,
                          backgroundColor: "#173c72",
                          border: "2.5px solid #ffffff",
                          boxShadow: "0 2px 6px rgba(15, 23, 42, 0.25)",
                          "&:hover": {
                            boxShadow: "0 0 0 7px rgba(23, 60, 114, 0.12)",
                          },
                          "&.Mui-active": {
                            boxShadow: "0 0 0 10px rgba(23, 60, 114, 0.18)",
                          },
                        },
                      }}
                    />
                  </div>
                  <div className="ref-product-price-range-limits">
                    <span>₹{formatPrice(rangeMin)}</span>
                    <span>₹{formatPrice(rangeMax)}</span>
                  </div>
                </div>
                {(minPrice !== undefined || maxPrice !== undefined) && (
                  <button
                    type="button"
                    className="ref-product-inline-clear"
                    onClick={() => {
                      setMinPrice(undefined);
                      setMaxPrice(undefined);
                      setLocalMin(rangeMin);
                      setLocalMax(rangeMax);
                    }}
                  >
                    Clear price
                  </button>
                )}
              </div>

              {/* Discount Buckets */}
              {facets.discountBuckets.length > 0 && (
                <div className="ref-product-filter-section">
                  <h2>Discount</h2>
                  <label className="ref-product-filter-option">
                    <input
                      type="radio"
                      name="ref-discount"
                      checked={minDiscount === undefined}
                      onChange={() => setMinDiscount(undefined)}
                    />
                    <span>Any discount</span>
                  </label>
                  {facets.discountBuckets.map((bucket) => (
                    <label
                      className="ref-product-filter-option"
                      key={bucket.minDiscount}
                    >
                      <input
                        type="radio"
                        name="ref-discount"
                        checked={minDiscount === bucket.minDiscount}
                        onChange={() => setMinDiscount(bucket.minDiscount)}
                      />
                      <span>
                        {bucket.label}{" "}
                        {bucket.count !== undefined && (
                          <small>({bucket.count})</small>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              )}

              {/* Customer Reviews */}
              <div className="ref-product-filter-section">
                <h2>Customer Reviews</h2>
                {facets.ratingBuckets.map((bucket) => (
                  <button
                    type="button"
                    className={`ref-product-rating-filter ${
                      minRating === bucket.minRating ? "active" : ""
                    }`}
                    key={bucket.minRating}
                    onClick={() =>
                      setMinRating(
                        minRating === bucket.minRating
                          ? undefined
                          : bucket.minRating,
                      )
                    }
                  >
                    <span className="ref-product-filter-stars">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          size={14}
                          fill={
                            star <= bucket.minRating
                              ? "currentColor"
                              : "none"
                          }
                        />
                      ))}
                    </span>
                    <span>
                      {bucket.label}
                      {bucket.count !== undefined && (
                        <small> ({bucket.count})</small>
                      )}
                    </span>
                  </button>
                ))}
              </div>

              {/* Brands */}
              {facets.brands.length > 0 && (
                <div className="ref-product-filter-section brand-section">
                  <h2>Brands</h2>
                  <div className="ref-product-brand-list">
                    {facets.brands.map((brand) => (
                      <label
                        className="ref-product-filter-option"
                        key={brand.name}
                      >
                        <input
                          type="checkbox"
                          checked={brandNames.includes(brand.name)}
                          onChange={() => toggleBrand(brand.name)}
                        />
                        <span>
                          {brand.name}{" "}
                          <small>({brand.count})</small>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </aside>

            {/* Results Grid Area */}
            <section className="ref-product-results">
              <div className="ref-product-results-head">
                <div>
                  <span>PRODUCTS FOR YOU</span>
                  <h2>{activeTabLabel}</h2>
                  <p>
                    {isInitialLoading
                      ? "Loading referral products…"
                      : `${totalItems} products available`}
                  </p>
                </div>
                <label className="ref-product-sort">
                  <span>Sort by</span>
                  <div>
                    <select
                      value={sort}
                      onChange={(event) =>
                        setSort(event.target.value as RefSort)
                      }
                    >
                      {SORT_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown size={16} />
                  </div>
                </label>
              </div>

              {error && allCards.length === 0 ? (
                <div className="ref-product-empty">
                  <AlertCircle size={30} />
                  <h3>Something went wrong</h3>
                  <p>{error}</p>
                  <button
                    type="button"
                    onClick={() => {
                      const targetTag =
                        REF_TABS.find((t) => t.id === activeTab)?.tag || "";
                      dispatch(
                        fetchReferralProducts(
                          targetTag ? { tag: targetTag } : undefined,
                        ),
                      );
                    }}
                  >
                    Try again
                  </button>
                </div>
              ) : isInitialLoading ? (
                <div className="ref-product-product-grid">
                  {Array.from({ length: 12 }).map((_, i) => (
                    <div
                      key={i}
                      className="men-product-card ref-product-skeleton"
                    />
                  ))}
                </div>
              ) : currentCards.length > 0 ? (
                <>
                  <div
                    className={`ref-product-product-grid ${
                      loading ? "is-refreshing" : ""
                    }`}
                  >
                    {currentCards.map((card) => {
                      const targetId = card.productId || card.id;
                      const isWishlisted = Boolean(
                        isItemWishlisted(targetId, card.ssin) ||
                          (card.id && isItemWishlisted(card.id, card.ssin)) ||
                          (card.ssin && isItemWishlisted(card.ssin)),
                      );
                      const selectedColor = selectedColors[card.id] ?? 0;
                      const isAdded = isProductInCart(card.productId || card.id);
                      const badge = card.dealLabel || (card.voucherAmount ? `VOUCHER` : null);
                      const deliveryType = card.nextDayDelivery || card.fastDelivery
                        ? "fast"
                        : card.freeDelivery
                        ? "delivery"
                        : "stock";
                      const deliveryLabel =
                        card.stockStatus === "out_of_stock"
                          ? "Out of stock"
                          : card.nextDayDelivery
                          ? "Next Day Delivery"
                          : card.fastDelivery
                          ? "Fast Delivery Available"
                          : card.freeDelivery
                          ? "Free Delivery"
                          : "In Stock";

                      return (
                        <article
                          className="men-product-card"
                          key={card.id}
                          role="button"
                          tabIndex={0}
                          onClick={(event) => {
                            const target = event.target as HTMLElement;
                            if (target.closest("button")) {
                              return;
                            }
                            handleProductClick(card);
                          }}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              handleProductClick(card);
                            }
                          }}
                        >
                          <div className="men-product-image-wrapper">
                            {badge && (
                              <span className="men-product-badge">
                                {badge}
                              </span>
                            )}
                            {/* <button
                              type="button"
                              className={`men-product-wishlist ${
                                isWishlisted ? "active" : ""
                              }`}
                              onClick={(e) => handleWishlistToggle(e, card)}
                              aria-label={
                                isWishlisted
                                  ? "Remove from wishlist"
                                  : "Add to wishlist"
                              }
                            >
                              <Heart
                                size={18}
                                strokeWidth={2}
                                fill={
                                  isWishlisted ? "currentColor" : "none"
                                }
                              />
                            </button> */}
                            <img
                              src={card.image}
                              alt={card.name}
                              className="men-product-image"
                              loading="lazy"
                              onError={(e) => {
                                (
                                  e.currentTarget as HTMLImageElement
                                ).src = "/assets/images/logos/salarlogo.png";
                              }}
                            />
                          </div>

                          <div className="men-product-info">
                            {card.colors.length > 0 && (
                              <div className="men-product-colors">
                                <div className="men-product-color-list">
                                  {card.colors
                                    .slice(0, 5)
                                    .map((color, index) => (
                                      <button
                                        type="button"
                                        key={`${color}-${index}`}
                                        className={`men-product-color ${
                                          selectedColor === index ? "active" : ""
                                        }`}
                                        title={color}
                                        aria-label={color}
                                        onClick={() =>
                                          selectColor(card.id, index)
                                        }
                                      >
                                        <span
                                          style={{
                                            backgroundColor: color,
                                          }}
                                        />
                                      </button>
                                    ))}
                                </div>
                                {card.colors.length > 5 && (
                                  <button
                                    type="button"
                                    className="men-product-more-colors"
                                    onClick={() => handleProductClick(card)}
                                  >
                                    {`+${card.colors.length - 5} more`}
                                  </button>
                                )}
                              </div>
                            )}

                            <h3>{card.name}</h3>
                            <p className="men-product-description">
                              {card.description || card.brandName}
                            </p>

                            <div className="men-product-rating">
                              {card.rating > 0 ? (
                                <>
                                  <strong>{card.rating.toFixed(1)}</strong>
                                  <span className="men-product-stars">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                      <Star
                                        key={star}
                                        size={13}
                                        strokeWidth={1.8}
                                        fill={
                                          star <= Math.round(card.rating)
                                            ? "currentColor"
                                            : "none"
                                        }
                                      />
                                    ))}
                                  </span>
                                  <span className="men-product-review-count">
                                    ({card.reviewCount})
                                  </span>
                                </>
                              ) : (
                                <span className="men-product-stars">
                                  {[1, 2, 3, 4, 5].map((star) => (
                                    <Star
                                      key={star}
                                      size={13}
                                      strokeWidth={1.8}
                                      fill="none"
                                    />
                                  ))}
                                </span>
                              )}
                            </div>

                            {(card.couponLabel || card.dealLabel) && (
                              <div
                                className={`men-product-deal ${
                                  card.voucherAmount ? "special" : "limited"
                                }`}
                              >
                                <span className="men-product-deal-dot" />
                                <strong>
                                  {card.couponLabel || card.dealLabel}
                                </strong>
                              </div>
                            )}

                            <div className="men-product-pricing">
                              <div className="men-product-price-main">
                                <strong>
                                  <sup>₹</sup>
                                  {formatPrice(card.price)}
                                </strong>
                                {card.discountPercent > 0 && (
                                  <span className="men-product-discount">
                                    {card.discountPercent}% OFF
                                  </span>
                                )}
                              </div>
                              {card.mrp > card.price && (
                                <div className="men-product-price-secondary">
                                  <span>MRP</span>
                                  <del>₹{formatPrice(card.mrp)}</del>
                                  <span className="men-product-saving">
                                    Save ₹{formatPrice(card.mrp - card.price)}
                                  </span>
                                </div>
                              )}
                            </div>

                          
                            <button
                              type="button"
                              className={`men-product-options ${
                                isAdded ? "added" : ""
                              }`}
                              onClick={() => handleAddToCart(card)}
                              disabled={card.stockStatus === "out_of_stock"}
                            >
                              {isAdded ? (
                                <>
                                  <Check size={16} strokeWidth={2.4} /> Added to
                                  Cart
                                </>
                              ) : (
                                <>
                                  <ShoppingCart size={16} /> Add to Cart
                                </>
                              )}
                            </button>
                          </div>
                        </article>
                      );
                    })}
                  </div>

                  {pageNumbers.length > 0 && (
                    <nav
                      className="ref-product-pagination"
                      aria-label="Referral products pagination"
                    >
                      <button
                        type="button"
                        onClick={() => goToPage(page - 1)}
                        disabled={page <= 1}
                        aria-label="Previous page"
                      >
                        <ChevronLeft size={16} />
                      </button>
                      {pageNumbers[0] > 1 && (
                        <>
                          <button type="button" onClick={() => goToPage(1)}>
                            1
                          </button>
                          {pageNumbers[0] > 2 && <span>…</span>}
                        </>
                      )}
                      {pageNumbers.map((n) => (
                        <button
                          type="button"
                          key={n}
                          className={n === page ? "active" : ""}
                          onClick={() => goToPage(n)}
                          aria-current={n === page ? "page" : undefined}
                        >
                          {n}
                        </button>
                      ))}
                      {pageNumbers[pageNumbers.length - 1] < totalPages && (
                        <>
                          {pageNumbers[pageNumbers.length - 1] <
                            totalPages - 1 && <span>…</span>}
                          <button
                            type="button"
                            onClick={() => goToPage(totalPages)}
                          >
                            {totalPages}
                          </button>
                        </>
                      )}
                      <button
                        type="button"
                        onClick={() => goToPage(page + 1)}
                        disabled={page >= totalPages}
                        aria-label="Next page"
                      >
                        <ChevronRight size={16} />
                      </button>
                    </nav>
                  )}
                </>
              ) : (
                <div className="ref-product-empty">
                  <Search size={30} />
                  <h3>No products match these filters</h3>
                  <p>
                    Clear one or more filters to continue browsing referral
                    products.
                  </p>
                  <button type="button" onClick={clearFilters}>
                    Clear Filters
                  </button>
                </div>
              )}
            </section>
          </div>
        </div>
      </main>

      {/* Mobile filter bottom-sheet */}
      {mobileFilterOpen && (
        <div
          className="ref-product-mobile-filter-layer"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setMobileFilterOpen(false);
            }
          }}
        >
          <section
            className="ref-product-mobile-filter-sheet"
            role="dialog"
            aria-modal="true"
            aria-label="Referral products filters"
          >
            <div className="ref-product-mobile-filter-handle" />

            <div className="ref-product-mobile-filter-header">
              <div>
                <span>REFINE PRODUCTS</span>
                <h2>Filters</h2>
              </div>
              <div className="ref-product-mobile-filter-header-actions">
                <button
                  type="button"
                  className="ref-product-mobile-clear-all"
                  onClick={clearFilters}
                >
                  Clear All
                </button>
                <button
                  type="button"
                  className="ref-product-mobile-filter-close"
                  onClick={() => setMobileFilterOpen(false)}
                  aria-label="Close filters"
                >
                  <X size={19} strokeWidth={2} />
                </button>
              </div>
            </div>

            <div className="ref-product-mobile-filter-content">
              <nav
                className="ref-product-mobile-filter-nav"
                aria-label="Filter categories"
              >
                {refProductMobileFilterSections.map((section) => {
                  const count = getMobileFilterCount(section.id);
                  return (
                    <button
                      type="button"
                      key={section.id}
                      className={
                        mobileFilterSection === section.id ? "active" : ""
                      }
                      onClick={() => {
                        setMobileFilterSection(section.id);
                        setMobileFilterSearch("");
                      }}
                    >
                      <span>{section.label}</span>
                      {count > 0 && <strong>{count}</strong>}
                    </button>
                  );
                })}
              </nav>

              <div className="ref-product-mobile-filter-panel">
                <div className="ref-product-mobile-filter-panel-title">
                  <span>
                    {
                      refProductMobileFilterSections.find(
                        (s) => s.id === mobileFilterSection,
                      )?.label
                    }
                  </span>
                  <strong>
                    {getMobileFilterCount(mobileFilterSection)} selected
                  </strong>
                </div>

                <label className="ref-product-mobile-filter-search">
                  <Search size={18} strokeWidth={2} />
                  <input
                    type="search"
                    value={mobileFilterSearch}
                    onChange={(event) =>
                      setMobileFilterSearch(event.target.value)
                    }
                    placeholder={`Search ${
                      refProductMobileFilterSections.find(
                        (s) => s.id === mobileFilterSection,
                      )?.label ?? "filters"
                    }`}
                  />
                  {mobileFilterSearch && (
                    <button
                      type="button"
                      onClick={() => setMobileFilterSearch("")}
                      aria-label="Clear filter search"
                    >
                      <X size={15} strokeWidth={2} />
                    </button>
                  )}
                </label>

                <div className="ref-product-mobile-filter-options-scroll">
                  {renderMobileFilterOptions()}
                </div>
              </div>
            </div>

            <div className="ref-product-mobile-filter-footer">
              <button
                type="button"
                className="ref-product-mobile-filter-reset"
                onClick={clearFilters}
              >
                Reset
              </button>
              <button
                type="button"
                className="ref-product-mobile-filter-apply"
                onClick={() => setMobileFilterOpen(false)}
              >
                See {totalItems} products
              </button>
            </div>
          </section>
        </div>
      )}
    </>
  );
};

export default RefProduct;
